/**
 * One photograph, as large as the screen allows.
 *
 * The avatar is forty pixels, which is enough to tell two people apart on a list and not enough
 * to check that the right face is on the right name — which is the one thing the office opens an
 * employee's page to do. The owner asked for it on 12 October: "admin have to see the full
 * picture of employees once admin click on pic."
 *
 * Deliberately not the app's Modal: that one is for asking a question and taking an answer. This
 * holds a picture, closes on anything (the backdrop, Escape, the button), and has nothing to
 * decide.
 */
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { t } from '../i18n/index.js';

export function PhotoViewer({
  src,
  name,
  onClose,
}: {
  src: string;
  /** Whose face it is — the caption, and what a screen reader is told. */
  name: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // The page behind must not scroll while a full-screen picture is over it; a phone otherwise
    // scrolls the list under the photograph when the finger moves at all.
    const had = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = had;
    };
  }, [onClose]);

  return createPortal(
    <div
      className="photo-viewer"
      role="dialog"
      aria-modal="true"
      aria-label={name}
      onClick={onClose}
    >
      <button type="button" className="photo-viewer-close" aria-label={t('close')}>
        <X size={20} aria-hidden="true" />
      </button>
      {/* The picture itself swallows the click, so tapping the face does not close it. */}
      <figure onClick={(e) => e.stopPropagation()}>
        <img src={src} alt={name} />
        <figcaption>{name}</figcaption>
      </figure>
    </div>,
    document.body,
  );
}
